import React, { useEffect } from 'react';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Trash2, Plus } from 'lucide-react';
import { useUserAiConfig, useUpdateUserAiConfig } from '../helpers/useUserSettings';
import { useAiModels } from '../helpers/useAiModels';
import { AiProviderTypeArrayValues } from '../helpers/schema';
import { Button } from './Button';
import { Input } from './Input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './Select';
import { Separator } from './Separator';
import { Skeleton } from './Skeleton';
import { UserSettingsAiFormLocalLlm } from './UserSettingsAiFormLocalLlm';
import styles from './UserSettingsAiForm.module.css';

// Schema definition matching the endpoint requirements but adapted for UI
const customModelSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  provider: z.string().min(1, 'Provider is required'),
  model: z.string(),
  endpoint: z.string().optional(),
  apiKey: z.string().optional(),
});

const formSchema = z.object({
  localLlmEnabled: z.boolean(),
  localLlmEndpoint: z.string().optional(),
  localLlmApiKey: z.string().optional(),
  localLlmModelsString: z.string().optional(), // UI helper for comma-separated list
  localLlmDefaultModel: z.string().optional(),
  preferredCloudProvider: z.enum(AiProviderTypeArrayValues).optional(),
  preferredCloudModel: z.string().optional(),
  customModels: z.array(customModelSchema).optional(),
});

type FormValues = z.infer<typeof formSchema>;

interface UserSettingsAiFormProps {
  onCancel: () => void;
  enabled?: boolean;
}

export const UserSettingsAiForm = ({ onCancel, enabled = true }: UserSettingsAiFormProps) => {
  const { data: config, isLoading } = useUserAiConfig();
  const { data: aiModelsData, isLoading: isLoadingModels } = useAiModels({ enabled });
  const { mutate: updateConfig, isPending: isSaving } = useUpdateUserAiConfig();

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      localLlmEnabled: false,
      localLlmEndpoint: 'http://localhost:11434',
      localLlmModelsString: 'llama3,mistral,codellama',
      customModels: [],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'customModels',
  });

  // Reset form when data loads
  useEffect(() => {
    if (config) {
      form.reset({
        localLlmEnabled: config.localLlmEnabled ?? false,
        localLlmEndpoint: config.localLlmEndpoint ?? 'http://localhost:11434',
        localLlmApiKey: config.localLlmApiKey ?? '',
        localLlmModelsString: Array.isArray(config.localLlmModels)
          ? config.localLlmModels.join(', ')
          : typeof config.localLlmModels === 'string'
            ? config.localLlmModels
            : '',
        localLlmDefaultModel: config.localLlmDefaultModel ?? '',
        preferredCloudProvider: config.preferredCloudProvider ?? undefined,
        preferredCloudModel: config.preferredCloudModel ?? '',

        customModels: (config.customModels as any[]) ?? [],
      });
    }
  }, [config, form]);

  const preferredProvider = form.watch('preferredCloudProvider');

  // Effect to validate/clear model selection when provider changes
  useEffect(() => {
    const currentModel = form.getValues('preferredCloudModel');
    if (!preferredProvider || !aiModelsData) return;

    // Filter relevant models to check validity
    const relevantModels = aiModelsData.cloudModels.filter((m) =>
      preferredProvider === 'ollama_cloud'
        ? m.provider === 'ollama_cloud'
        : m.provider === preferredProvider,
    );

    // If we have models for this provider but the current selection isn't one of them, we might want to warn or clear.
    // However, simply clearing might be annoying if data is still loading.
    // So we just ensure we don't block manual entry if needed, but for now we rely on user selection.
  }, [preferredProvider, aiModelsData, form]);

  const getFilteredModels = () => {
    if (!aiModelsData || !preferredProvider) return [];

    const cloudFiltered = aiModelsData.cloudModels.filter((m) => m.provider === preferredProvider);

    // Also include user's custom models that match the selected provider
    const customFiltered = (aiModelsData.customModels || [])
      .filter((m) => m.provider === preferredProvider)
      .map((m) => ({
        provider: m.provider,
        model: m.model,
        name: `${m.name} (Custom)`,
        tags: ['Custom'] as string[],
      }));

    return [...cloudFiltered, ...customFiltered];
  };

  const filteredModels = getFilteredModels();

  const onSubmit = (values: FormValues) => {
    // Transform UI values to API values
    const localLlmModels = values.localLlmModelsString
      ? values.localLlmModelsString
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean)
      : [];

    updateConfig({
      localLlmEnabled: values.localLlmEnabled,
      localLlmEndpoint: values.localLlmEndpoint || null,
      localLlmApiKey: values.localLlmApiKey || null,
      localLlmModels: localLlmModels,
      localLlmDefaultModel: values.localLlmDefaultModel || null,
      preferredCloudProvider: values.preferredCloudProvider || null,
      preferredCloudModel: values.preferredCloudModel || null,
      customModels: values.customModels || null,
    });
  };

  if (isLoading) {
    return (
      <div className={styles.loadingContainer}>
        <Skeleton className={styles.skeletonLine} />
        <Skeleton className={styles.skeletonBlock} />
        <Skeleton className={styles.skeletonLine} />
        <Skeleton className={styles.skeletonBlock} />
      </div>
    );
  }

  return (
    <form onSubmit={form.handleSubmit(onSubmit)} className={styles.form}>
      <div className={styles.scrollArea}>
        {/* Local LLM Section (Extracted) */}
        <UserSettingsAiFormLocalLlm
          control={form.control}
          setValue={form.setValue}
          watch={form.watch}
        />

        <Separator className={styles.separator} />

        {/* Cloud Preferences Section */}
        <section className={styles.section}>
          <h3 className={styles.sectionTitle}>Cloud Preferences</h3>
          <div className={styles.fieldsGrid}>
            <div className={styles.fieldGroup}>
              <label className={styles.label}>Provider</label>
              <Controller
                control={form.control}
                name="preferredCloudProvider"
                render={({ field }) => (
                  <Select
                    value={field.value || '__empty'}
                    onValueChange={(val) => field.onChange(val === '__empty' ? undefined : val)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select provider" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="openai">OpenAI</SelectItem>
                      <SelectItem value="anthropic">Anthropic</SelectItem>
                      <SelectItem value="google">Google Gemini</SelectItem>
                      <SelectItem value="ollama_cloud">Ollama Cloud</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              />
            </div>

            <div className={styles.fieldGroup}>
              <label className={styles.label}>Preferred Model ID</label>
              <Controller
                control={form.control}
                name="preferredCloudModel"
                render={({ field }) => (
                  <Select
                    value={field.value || '__empty'}
                    onValueChange={(val) => field.onChange(val === '__empty' ? '' : val)}
                    disabled={!preferredProvider || isLoadingModels}
                  >
                    <SelectTrigger>
                      <SelectValue
                        placeholder={
                          isLoadingModels
                            ? 'Loading models...'
                            : !preferredProvider
                              ? 'Select a provider first'
                              : 'Select a model'
                        }
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {filteredModels.length > 0 ? (
                        filteredModels.map((model) => (
                          <SelectItem key={model.model} value={model.model}>
                            {model.name || model.model}
                          </SelectItem>
                        ))
                      ) : (
                        <SelectItem value="__empty" disabled>
                          {isLoadingModels ? 'Loading...' : 'No models available for this provider'}
                        </SelectItem>
                      )}

                      {/* Allow custom entry fallback if needed? 
                          For now, we strictly follow the dropdown requirement. 
                          If users need arbitrary models, they can add Custom Models below. 
                      */}
                    </SelectContent>
                  </Select>
                )}
              />
            </div>
          </div>
        </section>

        <Separator className={styles.separator} />

        {/* Custom Models Section */}
        <section className={styles.section}>
          <div className={styles.sectionHeader}>
            <h3 className={styles.sectionTitle}>Custom Models</h3>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() =>
                append({
                  name: 'New Model',
                  provider: 'openai',
                  model: '',
                  endpoint: '',
                  apiKey: '',
                })
              }
            >
              <Plus size={14} /> Add Model
            </Button>
          </div>

          <div className={styles.customModelsList}>
            {fields.map((field, index) => (
              <div key={field.id} className={styles.customModelCard}>
                <div className={styles.customModelHeader}>
                  <span className={styles.customModelIndex}>#{index + 1}</span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    className={styles.deleteButton}
                    onClick={() => remove(index)}
                  >
                    <Trash2 size={14} />
                  </Button>
                </div>
                <div className={styles.fieldsGrid}>
                  <div className={styles.fieldGroup}>
                    <label className={styles.label}>Display Name</label>
                    <Input
                      {...form.register(`customModels.${index}.name` as const)}
                      placeholder="My Custom Model"
                    />
                  </div>
                  <div className={styles.fieldGroup}>
                    <label className={styles.label}>Provider Type</label>
                    <Input
                      {...form.register(`customModels.${index}.provider` as const)}
                      placeholder="openai"
                    />
                  </div>
                  <div className={styles.fieldGroup}>
                    <label className={styles.label}>Model ID</label>
                    <Input
                      {...form.register(`customModels.${index}.model` as const)}
                      placeholder="model-id"
                    />
                  </div>
                  <div className={styles.fieldGroup}>
                    <label className={styles.label}>Endpoint (Optional)</label>
                    <Input
                      {...form.register(`customModels.${index}.endpoint` as const)}
                      placeholder="https://api.example.com/v1"
                    />
                  </div>
                  <div className={styles.fieldGroupFull}>
                    <label className={styles.label}>API Key (Optional)</label>
                    <Input
                      type="password"
                      {...form.register(`customModels.${index}.apiKey` as const)}
                      placeholder="sk-..."
                    />
                  </div>
                </div>
              </div>
            ))}
            {fields.length === 0 && (
              <div className={styles.emptyState}>No custom models configured.</div>
            )}
          </div>
        </section>
      </div>

      <div className={styles.footer}>
        <Button type="button" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" disabled={isSaving}>
          {isSaving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>
    </form>
  );
};
